import { useCallback, useEffect, useMemo, useState } from "react";
import CustomForm from "../common/components/CustomForm";
import GenderDropdown from "../common/components/GenderDropdown";
import { Col, Row } from "../common/components/Grid";
import NumericInput from "../common/components/NumericInput";
import useClientConfig from "../common/hooks/clientConfigHook";
import { useLevel } from "../reducers/levelSlice";
import { useSite } from "../reducers/siteSlice";
import { useType } from "../reducers/typeSlice";
import EventDropdown from "./EventDropdown";
import GridInput from "./GridInput";
import LevelDropdown from "./LevelDropdown";
import LocationInput from "./LocationInput";
import PriorityDropdown from "./PriorityDropdown";
import ProblemInput from "./ProblemInput";
import SiteDropdown from "./SiteDropdown";
import TypeDropdown from "./TypeDropdown";
import PropTypes from 'prop-types';
import TagManager from "../common/components/TagManager";
import { useJobTags } from "../reducers/jobTagSlice";
import { useProblem } from "../reducers/problemSlice";
import TextareaInput from "../common/components/TextareaInput";
import Icon from "../common/components/Icon";
import useNetwork from "../common/hooks/networkHook";
import { FloatingLabel } from "../common/components/FloatingLabel";
import { Alert, Button } from "react-bootstrap";
import Label from "../common/components/Label";
import PositionInput from "./PositionInput";
import ResultInput from "./ResultInput";
import { AIThinking } from "./AIThinking";
import JobStatusDropdown from "./JobStatusDropdown";
import { requestAssistance, useJobAssistance } from "../reducers/jobAssistanceSlice";
import ChannelDropdown from "./ChannelDropdown";
import PhoneInput from "../common/components/PhoneInput";
import EmailInput from "../common/components/EmailInput";
import TextInput from "../common/components/TextInput";
import { deepClone } from "../common/utils/objectUtilities";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";
import { checkEnabled, checkEnabledValue } from "../utils/enableUtilities";
import TabBar from "../common/components/TabBar";
import { safeEval } from "../common/utils/safeEval";
import { canUpdateJob } from "../reducers/jobSlice";
import useCredentials from "../common/hooks/credentialsHook";
import { usePriority } from "../reducers/prioritySlice";

const InitialNote = ({ job, onChange }) => {
    const { t } = useSpecializedTranslation(job);
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const [aiSuggestions, setAISuggestions] = useState();
    const [aiSuggestTimeout, setAISuggestTimeout] = useState();

    const runAISuggest = useCallback((event, note) => {
        if (clientConfig.ai?.suggestions) {
            if (event.altKey && (event.key >= '1' && event.key <= '9')) {
                //apply suggestion
                const suggestionNumber = parseInt(event.key) - 1;
                if (suggestionNumber < aiSuggestions?.length) {
                    const s = aiSuggestions[suggestionNumber];
                    const delta = { type: s.type, problem: s.problem, priority: s.priority, }
                    if (clientConfig.enableSites) {
                        delta.site = s.site;
                    }
                    if (clientConfig.enableLevels) {
                        delta.level = s.level;
                    }
                    if (s.generalcustomfields) {
                        delta.generalcustomfields = s.generalcustomfields;
                    }
                    if (s.customfields) {
                        delta.customfields = s.customfields;
                    }
                    if (clientConfig.enableBio) {
                        if (s.gender) {
                            delta.gender = s.gender;
                        }
                        if (s.age) {
                            delta.age = s.age;
                        }
                    }
                    if (checkEnabled(clientConfig, clientConfig.assistanceFirstClass, job) && s.assistance) {
                        delta.assistance = deepClone(s.assistance);
                    }
                    onChange(delta);
                }
            } else if (!event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey && (event.key === 'Enter')) {
                event.stopPropagation?.();
                event.preventDefault?.();
                if (clientConfig.enableSites && !job.site) {
                    return;
                }
                if (clientConfig.enableLevels && !job.level) {
                    return;
                }
                if (aiSuggestTimeout) {
                    clearTimeout(aiSuggestTimeout);
                    setAISuggestTimeout();
                }
                if (!job.problem && note?.trim()?.length > 0) {
                    setAISuggestTimeout(setTimeout(async () => {
                        let data = await network.postData('/ai/classify', JSON.stringify({ situation: note, type: job.type, site: job.site, level: job.level }), { 'Content-Type': 'application/json', Accept: 'application/json' });
                        setAISuggestions(data);
                        setAISuggestTimeout();
                    }, 250));
                } else if (aiSuggestions?.length > 0) {
                    setAISuggestions();
                }
            }
        }
    }, [clientConfig, job, onChange, aiSuggestions, aiSuggestTimeout, network]);

    const applySuggestion = useCallback(s => {
        const delta = {};
        Object.keys(s).forEach(f => {
            if (s[f]) {
                delta[f] = s[f];
            }
        })
        onChange(delta);
    }, [onChange]);

    const applyLocation = useCallback(s => {
        const delta = { location: s.location };
        ['gridx','gridy','latitude','longitude'].forEach(f => {
            if (s[f]) {
                delta[f] = s[f];
            }
        });
        onChange(delta);
    }, [onChange]);

    let suggestions = null;
    if (aiSuggestions?.length > 0) {
        suggestions = <FloatingLabel hideLabel={true}><Alert variant="info">
            <table>
                <thead>
                    <tr>
                        <th></th>
                        { clientConfig.enableSites && <th><span className="p-1 px-3">{t('site', { count: 1 })}</span></th> }
                        { clientConfig.enableLevels && <th><span className="p-1 px-3">{t('level', { count: 1 })}</span></th> }
                        <th><span className="p-1 px-3">{t('type', { count: 1 })}</span></th>
                        <th><span className="p-1 px-3">{t('problem', { count: 1 })}</span></th>
                        <th><span className="p-1 px-3">{t('location', { count: 1 })}</span></th>
                    </tr>
                </thead>
                <tbody>
                    {aiSuggestions.map((s, idx) => <tr>
                        <td><Button variant="link" style={{ color: 'inherit' }} onClick={() => applySuggestion(s)}>{idx + 1}.</Button></td>
                        { clientConfig.enableSites && <td><Button variant="link" style={{ color: 'inherit' }} onClick={() => onChange({ site: s.site })}>{s.site}</Button></td> }
                        { clientConfig.enableLevels && <td><Button variant="link" style={{ color: 'inherit' }} onClick={() => onChange({ level: s.level })}>{s.level}</Button></td> }
                        <td><Button variant="link" style={{ color: 'inherit' }} onClick={() => onChange({ type: s.type })}>{s.type}</Button></td>
                        <td><Button variant="link" style={{ color: 'inherit' }} onClick={() => onChange({ problem: s.problem, priority: s.priority })}>{s.problem}</Button></td>
                        <td>{ s.location ? <Button variant="link" style={{ color: 'inherit' }} onClick={() => applyLocation(s)}>{s.location}</Button> : <span className="p-1 px-3">{t('unknown')}</span> }</td>
                    </tr>)}
                </tbody>
            </table>
        </Alert></FloatingLabel>
    } else if (aiSuggestions?.length === 0) {
        suggestions = <Row><Col>{t('aiNoSuggestions')}</Col></Row>
    }

    return <><Row>
        <Col>
        <TextareaInput placeholder={t('initialNotes')} autoCapitalize="none" value={job.note} onChange={note => onChange({ note })} 
            onKeyDown={event => runAISuggest(event, event.target.value)} maxLength={1024} rows={3}
            appendCtrl={clientConfig.ai?.suggestions && <Button style={{ height: '100%' }} variant="default" disabled={(clientConfig.enableSites && !job.site) || (clientConfig.enableLevels && !job.level)} onClick={event => { event.key = 'Enter'; runAISuggest({ ...event, key: 'Enter' }, job.note); }} title={t('suggestJobFields')}><Icon variant="solid" kind="fa-infinity"></Icon></Button>}></TextareaInput>
        </Col>
    </Row>
    { clientConfig.ai.suggestions && ((clientConfig.enableSites && !job.site) || (clientConfig.enableLevels && !job.level)) && <Row>
        <Col>
            <Label text={t('aiSuggestInstructions' + (clientConfig.enableSites ? t('site', {count: 1}) : '') + (clientConfig.enableLevels ? t('level', { count: 1 }) : ''))}></Label>
        </Col>
    </Row> }
    { aiSuggestTimeout && <AIThinking maxHeight="30px"></AIThinking> }
    { clientConfig.ai.suggestions ? suggestions : null }
    </>;
};

InitialNote.propTypes = {
    job: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired
}

const EMPTY_ARRAY = [];

/**
 * A component to view / edit a single job's core information
 * @component
 */
const JobDetails = ({ job, isSearch = false, disabled = false, onChange, disableAutoHide, registerSection }) => {
    const { t } = useSpecializedTranslation(job);
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const site = useSite(job.site);
    const level = useLevel(job.site, job.level);
    const type = useType(job.site, job.level, job.type);
    const problem = useProblem(job.site, job.level, job.type, job.problem);
    const priority = usePriority(job.priority);
    const jobTags = useJobTags();
    const assistance = useJobAssistance(job.jobid);

    const siteRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.permissiveTransfer?.restriction, ["site", "level"], job), [clientConfig, job]);
    const levelRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.permissiveTransfer?.restriction, ["level"], job), [clientConfig, job]);
    
    const additionalTypesDisplayValues = useMemo(() =>
        job?.jobid && clientConfig.permissiveTransfer?.enable ? 
            assistance.filter(a => (!clientConfig.enableSites || !siteRestricted || a.site === job.site) && (!clientConfig.enableLevels || !levelRestricted || a.level === job.level)) 
        : EMPTY_ARRAY
    , [assistance, clientConfig, siteRestricted, job, levelRestricted]);

    const generalCustomfields = useMemo(() => clientConfig.jobGeneralFormSpec?.filter?.(section => section.search === undefined || !!isSearch === !!section.search), [clientConfig.jobGeneralFormSpec, isSearch]);
    const siteCustomfields = useMemo(() => site?.formdef?.filter?.(section => section.search === undefined || !!isSearch === !!section.search), [site, isSearch]);
    const levelCustomfields = useMemo(() => level?.formdef?.filter?.(section => section.search === undefined || !!isSearch === !!section.search), [level, isSearch]);
    const typeCustomfields = useMemo(() => type?.formdef?.filter?.(section => section.search === undefined || !!isSearch === !!section.search), [type, isSearch]);
    const problemCustomFields = useMemo(() => problem?.formdef?.filter?.(section => section.search === undefined || !!isSearch === !!section.search), [problem, isSearch]);
    const priorityCustomFields = useMemo(() => priority?.formdef?.filter?.(section => section.search === undefined || !!isSearch === !!section.search), [priority, isSearch]);
    const handleProblemUpdate = useCallback(v => {
        if (disabled) {
            return;
        }
        const delta = { problem: v.problem };
        if (!isSearch) {
            if (v.type) {
                delta.type = v.type;
            }
            if (v.priority) {
                delta.priority = v.priority;
            }
            if (clientConfig.enableLevels && v.level) {
                delta.level = v.level;
            }
            if (clientConfig.enableSites && v.site) {
                delta.site = v.site;
            }
            if (checkEnabled(clientConfig, clientConfig.enableAssistance, job) && v.assistance) {
                if (job.jobid) {
                     requestAssistance(clientConfig, network, v.assistance, job.jobid);
                } else if (checkEnabled(clientConfig, clientConfig.assistanceFirstClass, job)) {
                    if (!job.assistance || job.assistance.length === 0) {
                        delta.assistance = v.assistance;
                    } else {
                        delta.assistance = [...job.assistance]
                        job.assistance.forEach(a => {
                            if (job.assistance.every(a2 => a2.type !== a.type && (!clientConfig.enableLevels || a2.level !== a.level) && (!clientConfig.enableSites || a2.site !== a.site))) {
                                delta.assistance.push(a);
                            }
                        });
                    }
                }
            }
        }
        onChange(delta);
    }, [clientConfig, network, onChange, isSearch, disabled, job]);
    const handleTypeUpdate = useCallback(v => {
        const delta = { type: v.type }
        if (clientConfig.enableSites && v.site) {
            delta.site = v.site
        }
        if (clientConfig.enableLevels && v.level) {
            delta.level = v.level;
        }
        onChange(delta);
    }, [onChange, clientConfig.enableSites, clientConfig.enableLevels])
    const handleLevelUpdate = useCallback(v => {
        const delta = { level: v.level };
        if (clientConfig.enableSites && v.site) {
            delta.site = v.site;
        }
        onChange(delta)
    }, [onChange, clientConfig.enableSites]);

    const handleLocationUpdate = useCallback(delta => {
        if (clientConfig.locationMode === 'address') {
            const update = {};
            if (delta.details && Object.keys(delta.details).length > 0) {
                update.customfields = { __location_details__: delta.details };
            }
            if (!isSearch) {
                update.location = delta.location;
                Object.keys(delta).filter(f => !['location','details'].includes(f)).forEach(f => update[f] = delta[f]);
            }
            onChange(update);
        } else {
            onChange(isSearch ? { location: delta.location } : delta)
        }
    }, [clientConfig.locationMode, onChange, isSearch])

    const bioControls = clientConfig.enableBio ? <><Col md={2}>
            <NumericInput id="jobDetails_ageInput" live={isSearch} value={job.age} placeholder={t('age')} min={0} step={1} onChange={value => onChange({ age: value })} disabled={disabled}></NumericInput>
        </Col>
        <Col md={2}>
            <GenderDropdown id="jobDetails_genderDropdown" gender={job.gender} placeholder={t('gender')} onChange={value => onChange({ gender: value })} disabled={disabled}></GenderDropdown>
        </Col></> : null;
    
    const statusControls = clientConfig.enableJobStatus && <Col md={4}><JobStatusDropdown value={job?.status ?? 'New'} placeholder={t('status_one')} onChange={value => onChange({ status: value })} disabled={disabled} readonly={!job?.jobid && !isSearch} isSearch={isSearch}></JobStatusDropdown></Col>;
    const positionControls = clientConfig.enableLoc && <PositionInput live={isSearch} position={job} disabled={disabled} onChange={onChange} disableRevCode={isSearch || disabled || clientConfig.locationMode !== 'simple'}></PositionInput>;
    const gridControls = clientConfig.enableGrid && <GridInput id="jobDetails_gridInput" live={isSearch} gridx={job.gridx} gridy={job.gridy} placeholder={{ x: t('gridX'), y: t('gridY') }} onChange={onChange} disabled={disabled}></GridInput>;
    const resultControl = job?.jobid && clientConfig.enableResult && <ResultInput live={isSearch} value={job.result} disabled={disabled} onChange={onChange} site={job.site} level={job.level} type={job.type} required={priority && priority.value >= clientConfig.enableResultPriority} placeholder={t('result', { count: 1 })}></ResultInput>;
    const context = useMemo(() => ({ job, assistance }), [job, assistance]);

    const [outlineSections, setOutlineSections] = useState();
    const inlineGeneralCustomfields = useMemo(() => {
        return generalCustomfields?.filter(section => section.inline || (!section.tab && (section.borderless || !section.title)));
    }, [generalCustomfields]);

    const inlineCustomfields = useMemo(() => {
        const inlineCustomfields = [];
        for (const form of [siteCustomfields, levelCustomfields, typeCustomfields, problemCustomFields, priorityCustomFields]) {
            if (form) {
                inlineCustomfields.push(...form.filter(section => section.inline || (!section.tab && (section.borderless || !section.title))));
            }
        }
        return inlineCustomfields;
    }, [siteCustomfields, levelCustomfields, typeCustomfields, problemCustomFields, priorityCustomFields]);

    useEffect(() => {
        const outlineSections = [];
        if (generalCustomfields?.length > 0) {
            const outlineSectionMap = {};
            for (const section of generalCustomfields.filter(section => !section.inline && (section.tab || (!section.borderless && section.title)))) {
                const tabID = section.tab ?? (section.uuid || section.id);
                if (!tabID) {
                    continue;
                }
                if (!outlineSectionMap[tabID]) {
                    outlineSectionMap[tabID] = { id: tabID, def: [], dest: 'generalcustomfields' };
                }
                if (!section.tab || section.tab === section.uuid || section.tab === section.id) {
                    outlineSectionMap[tabID].label = section.title;
                    outlineSectionMap[tabID].icon = section.icon;
                    outlineSectionMap[tabID].shortname = section.shortname;
                    outlineSectionMap[tabID].cond = section.cond;
                    outlineSectionMap[tabID].sequence = section.sequence
                    outlineSectionMap[tabID].def.push({ ...section, borderless: true });
                } else {
                    outlineSectionMap[tabID].def.push(section);
                }
            }
            outlineSections.push(...Object.values(outlineSectionMap));
        }
        {
            const outlineSectionMap = {}
            for (const form of [siteCustomfields, levelCustomfields, typeCustomfields, problemCustomFields, priorityCustomFields]) {
                if (form) {
                    for (const section of form.filter(section => !section.inline && (section.tab || (!section.borderless && section.title)))) {
                        const tabID = section.tab ?? (section.uuid || section.id);
                        if (!tabID) {
                            continue;
                        }
                        if (!outlineSectionMap[tabID]) {
                            outlineSectionMap[tabID] = { id: tabID, def: [], dest: 'customfields' };
                        }
                        if (!section.tab || section.tab === section.uuid || section.tab === section.id) {
                            outlineSectionMap[tabID].label = section.title;
                            outlineSectionMap[tabID].icon = section.icon;
                            outlineSectionMap[tabID].shortname = section.shortname;
                            outlineSectionMap[tabID].cond = section.cond;
                            outlineSectionMap[tabID].sequence = section.sequence
                            outlineSectionMap[tabID].def.push({ ...section, borderless: true });
                        } else {
                            outlineSectionMap[tabID].def.push(section);
                        }
                    }
                }
            }
            outlineSections.push(...Object.values(outlineSectionMap));
        }
        outlineSections.forEach((section, i) => {
            const EMPTY = {}
            section.enabled = () => !section.cond || safeEval(`(section, data) => ${section.cond}`)(section.def, job[section.dest] ?? EMPTY);
            section.render = () => <CustomForm live={isSearch} def={section.def} data={job[section.dest] ?? EMPTY} onChange={value => onChange({ [section.dest]: value })} required={true} disabled={disabled} context={context}></CustomForm>;
            if (!section.sequence) {
                section.sequence = i;
            }
        });
        if (registerSection) {
            const cleanup = outlineSections.map(section => registerSection(section));
            return () => cleanup.forEach(c => c());
        } else {
            outlineSections.sort((a, b) => a.sequence - b.sequence);
            setOutlineSections(outlineSections);
        }
    }, [generalCustomfields, siteCustomfields, levelCustomfields, typeCustomfields, problemCustomFields, priorityCustomFields, job, isSearch, onChange, disabled, context, registerSection]);
    
    return <>
    {
        !job.jobid && !isSearch && !disabled && clientConfig.noteFirstClass && <InitialNote job={job} onChange={onChange}></InitialNote>
    }
    {
        (clientConfig.enableSites || clientConfig.enableLevels || clientConfig.enableEvents) && <Row>
            <Col md={4}>
                {
                    (clientConfig.enableSites || clientConfig.enableLevels) && <Row>
                        {clientConfig.enableSites && <Col md><SiteDropdown id="jobDetails_site" site={job.site} placeholder={t('site_one')} onChange={onChange} perm={isSearch ? 'read' : 'write'} required={!isSearch} disabled={disabled} autoHide={!disableAutoHide && clientConfig.enableMinimalJobControls}></SiteDropdown></Col>}
                        {clientConfig.enableLevels && <Col md><LevelDropdown id="jobDetails_level" site={job.site} level={job.level} placeholder={t('level_one')} perm={isSearch ? 'read' : 'write'} onChange={handleLevelUpdate} required={!isSearch} disabled={disabled} autoHide={!disableAutoHide && clientConfig.enableMinimalJobControls}></LevelDropdown></Col>}
                    </Row>
                }
            </Col>
            <Col md={8}>
                {clientConfig.enableEvents === true && <EventDropdown id="jobDetails_event" site={job.site} level={job.level} type={job.type} event={job.event} perm={isSearch ? 'read' : 'write'} onChange={onChange} required={!isSearch} disabled={disabled} isSearch={isSearch}></EventDropdown>}
                {clientConfig.enableEvents === 'freetext' && <TextInput id="jobDetails_event" value={job.event_free} placeholder={t('event_one')} disabled={disabled} required={!!isSearch} onChange={event_free => onChange({ event_free })} maxLength={128}></TextInput>}
            </Col>
        </Row>
    }
    <Row>
        <Col md={4}>
            <TypeDropdown id="jobDetails_type" site={job.site} level={job.level} placeholder={t('type_one')} type={job.type} additionalTypes={additionalTypesDisplayValues} perm={isSearch ? 'read' : 'write'} mode={isSearch ? undefined : job.jobid ? 'transfer' : 'create'} onChange={handleTypeUpdate} required={!isSearch} disabled={disabled} autoHide={!disableAutoHide && clientConfig.enableMinimalJobControls}></TypeDropdown>
        </Col>
        <Col md={8}>
            <ProblemInput id="jobDetails_problem" live={isSearch} site={job.site} level={job.level} type={job.type} priority={job.priority} problem={job.problem} placeholder={t('problem_one')} onChange={handleProblemUpdate} required={!isSearch} disabled={disabled}></ProblemInput>
        </Col>
    </Row>
    <Row>
        <Col md={4}>
            <PriorityDropdown id="jobDetails_priority" priority={job.priority} onChange={onChange} required={!isSearch} isSearch={isSearch} placeholder={t('priority_one')} disabled={disabled} site={job.site} level={job.level} type={job.type}></PriorityDropdown>
        </Col>
        <Col md={8}>
            <LocationInput id="jobDetails_location" mode={clientConfig.locationMode} live={isSearch} site={job.site} level={job.level} type={job.type} location={job.location} placeholder={clientConfig.locationMode === 'simple' ? t('location_one') : undefined} details={job.customfields?.__location_details__} latitude={job.latitude} longitude={job.longitude} onChange={handleLocationUpdate} required={!isSearch} disableGeocode={isSearch} disabled={disabled}></LocationInput>
        </Col>
    </Row>
    <Row>
        { statusControls || bioControls || <Col md={4}></Col>}
        <Col md={8}>
            { gridControls || positionControls }
        </Col>
    </Row>
    <Row>
        { statusControls ? (bioControls || <Col md={4}></Col>) : <Col md={4}></Col> }
        <Col md={8}>
            { gridControls && positionControls ? positionControls : resultControl }
        </Col>
    </Row>
    { gridControls && positionControls && <Row>
        <Col md={4}></Col>
        <Col md={8}>{resultControl}</Col>
    </Row>}
    { clientConfig.enableChannels && <Row>
        <Col md={4}>
            <Row>
                <Col md>
                    <ChannelDropdown value={job.channel} placeholder={t('channel')} onChange={channel => onChange({ channel })} disabled={disabled}></ChannelDropdown>
                </Col>
                <Col md>
                    {(job.channel === 'SMS' || job.channel === 'Voice') && <PhoneInput value={job.channeldetails} placeholder={t('phone')} onChange={channeldetails => onChange({ channeldetails })} disabled={disabled}></PhoneInput>}
                    {job.channel === 'Email' && <EmailInput value={job.channeldetails} placeholder={t('email')} disabled={disabled} onChange={channeldetails => onChange({ channeldetails })}></EmailInput> }
                </Col>
            </Row>
        </Col>
    </Row>}
    { checkEnabled(clientConfig, clientConfig.enableJobTags, job) && <Row>
        <Col>
            <TagManager id="jobDetails_tagManager" tags={job.tags} values={jobTags} placeholder={t('tag', { count: 100 })} labelField="tag" valueField="id" disabled={disabled} onChange={value => onChange({ tags: value })}></TagManager>
        </Col>
    </Row> }
    { inlineGeneralCustomfields?.length > 0 && <Row>
        <Col>
            <CustomForm live={isSearch} def={inlineGeneralCustomfields} data={job.generalcustomfields ?? {}} onChange={value => onChange({ generalcustomfields: value })} required={true} disabled={disabled} context={context}></CustomForm>
        </Col>
    </Row> }
    { inlineCustomfields?.length > 0 && <Row>
        <Col>
            <CustomForm live={isSearch} def={inlineCustomfields} data={job.customfields ?? {}} onChange={value => onChange({ customfields: value })} required={true} disabled={disabled} context={context}></CustomForm>
        </Col>
    </Row> }
    { !registerSection && outlineSections?.length > 0 && <TabBar id="jobDetailTabs" buttons={outlineSections} showLabels enableCollapse t={t}></TabBar> }
    </>
}

JobDetails.propTypes = {
    /**
     * The job to be rendered by the control
     */
    job: PropTypes.object,

    /**
     * Whether this control is being used for a search or for editing a job
     * this controls a number of settings in terms of control options and
     * editability.
     */
    isSearch: PropTypes.bool,

    /**
     * Force the control to render with edit capability removed
     */
    disabled: PropTypes.bool,

    /**
     * A callback to be called with a delta when the job is changed
     */
    onChange: PropTypes.func
}

export default JobDetails